/*
 * FIX-02 — live data for the real, admin-managed proof pages: Projects, Team, and the
 * admin-swappable hero-image slots.
 *
 * Same "fetch after hydration" pattern already used for Reviews/Affiliations (see AboutPage.js's
 * Affiliations component, lib/siteData.js's useReviewSummary): the pre-rendered HTML shows the
 * honest starting state (empty projects list / site-default images), and the live data — which
 * can change at any time from the admin panel — swaps in once the client-side fetch resolves.
 * Nothing here is baked into the build; Projects and Team are never seeded with fake data.
 */
import { useEffect, useState } from 'react';
import { api } from './api';

const useLiveList = (path) => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get(path)
      .then(({ data }) => { if (alive) setItems(Array.isArray(data?.items) ? data.items : []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [path]);
  return { items, loaded };
};

/** Published projects, newest first (same shape the admin editor saves). */
export const useProjects = () => useLiveList('/projects');

/** Team members, display order. */
export const useTeam = () => useLiveList('/team');

/** "SS" from "Sebastian Suciu" — initials monogram shown until a real photo is uploaded. */
export const initialsOf = (name) => (name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

/** "October 2026" from a stored "YYYY-MM" completed_date, or '' if unset/invalid. */
export const monthYear = (completedDate) => {
  if (!completedDate) return '';
  const [y, m] = completedDate.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

// One shared fetch of all 6 hero-image slots per page load (useHeroImage can be called several
// times on the same page — homepage alone uses it 5 times).
let heroPromise = null;
const loadHeroSlots = () => {
  if (!heroPromise) {
    heroPromise = api.get('/hero-images').then(({ data }) => data?.slots || {}).catch(() => ({}));
  }
  return heroPromise;
};

/**
 * One admin-swappable image slot. Always returns the site's own default immediately (so the
 * pre-rendered HTML and first paint — including hero fetchpriority="high" — are unaffected in the
 * common case where no admin override exists); swaps in the live override, if any, once the
 * `/api/hero-images` fetch resolves.
 */
export const useHeroImage = (slot, fallbackSrc, fallbackAlt) => {
  const [override, setOverride] = useState(null);
  useEffect(() => {
    let alive = true;
    loadHeroSlots().then((slots) => { if (alive && slots[slot]?.url) setOverride(slots[slot]); });
    return () => { alive = false; };
  }, [slot]);
  if (override) {
    return { src: override.url, alt: override.alt || fallbackAlt, width: override.width, height: override.height, overridden: true };
  }
  return { src: fallbackSrc, alt: fallbackAlt, width: undefined, height: undefined, overridden: false };
};
